import * as Api from "@api";
import * as C from "@components";
import clsx from "clsx";
import React from "react";
import css from "./product-customizations-section.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";

const ProductCustomizationsSectionBaseKeys = [
    "customizations.headline",
    "customizations.body",
] as const;

export const ProductCustomizationsSectionKeys = [
    ...ProductCustomizationsSectionBaseKeys,
    "customizations.link",
] as const;

export interface ProductCustomizationsItem extends React.ComponentProps<typeof VadImage>
{
    bgColor: C.Color | string;
    hiddenOnMobile?: boolean;
}

export interface ProductCustomizationsSectionProps
{
    height: C.Height | C.MediaSpecific<C.Height | C.LayoutHeight>;
    items: ProductCustomizationsItem[];
    className?: string;
    customId?: string;
    customHref?: string;
    hiddenOnMobile?: boolean;
    hiddenOnDesktop?: boolean;
    pageContent?: Api.Content<typeof ProductCustomizationsSectionKeys[number]>;
    itemsEnd?: boolean;
}

export const ProductCustomizationsSection = ({ height, items, pageContent, hiddenOnDesktop, hiddenOnMobile, className, customId, customHref, itemsEnd = false }: ProductCustomizationsSectionProps): React.FunctionComponentElement<ProductCustomizationsSectionProps> =>
{
    const mobileItems = items.filter((item) => item.hiddenOnMobile !== true);

    const classes = clsx(
        {
            [css["hidden-on-mobile"]]: hiddenOnMobile,
            [css["hidden-on-desktop"]]: hiddenOnDesktop,
        },
        className,
    );

    return (
        <C.PageSection
            className={classes}
            {...(!!customId ? {id: customId} : {} )}
        >
            {pageContent && <C.SideBySideText
                className={css["side-by-side-text"]}
                items={{
                    "customizations.headline": { typography: C.Typography.H2, maxWidth: C.Width.W648 }
                }}
                keys={ProductCustomizationsSectionBaseKeys}
                layout={{ all: { margins: { bottom: C.Margin.D40 } } }}
                nodes={{
                    "customizations.body":
                        <>
                            <C.PageContent {...pageContent["customizations.body"]} margins={{ bottom: C.Margin.D20 }} typography={C.Typography.P2} maxWidth={C.Width.W448} className={css["customizations-body"]}/>
                            {pageContent["customizations.link"]?.data && <C.PageLink href="/classic-customizations" variant={C.PageLinkVariant.Link} className={css["desktop-see-page-link"]}>
                                {pageContent["customizations.link"].data}
                            </C.PageLink>}
                        </>
                }}
                pageContent={pageContent}
            />}
            <C.Card className={clsx(css[`card-${items.length}`], itemsEnd ? 'h-[240px] lg:h-[360px]' : C.heightClass(height), css["items_desktop_only"])}>
                {items.map((item, index) =>
                    {
                        const { bgColor, style, hiddenOnMobile, ...imageProps } = item;
                        
                        const figureProps = C.colorProps(bgColor, C.ColorType.Background);
                        
                        const styles = {
                            ...figureProps.style,
                            ...style
                        };

                        const classes = figureProps.className
                        
                        return (
                            <figure key={index} className={`${classes} ${itemsEnd ? 'flex' : ''}`} style={
                                itemsEnd ? {
                                    paddingBottom: '6%',
                                    alignItems: 'end',
                                    ...styles,
                                } : styles
                            }>
                                <VadImage {...imageProps} />
                            </figure>
                        )
                })}
            </C.Card>
            <C.Card className={clsx(css[`card-${mobileItems.length}`], css["items_mobile_only"])}>
                {mobileItems.map((item, index) =>
                    {
                        const { bgColor, style, hiddenOnMobile, ...imageProps } = item;
                        
                        const figureProps = C.colorProps(bgColor, C.ColorType.Background);
                        
                        const styles = {
                            ...figureProps.style,
                            ...style
                        };

                        const classes = clsx(
                            figureProps.className,
                            C.heightClass(height),
                            css[`cards-${item.mobileHeight ?? 170}`]
                        );

                        return (
                            <figure key={index} className={classes} style={styles}>
                                <VadImage
                                    {...imageProps}
                                    mobileSrc={imageProps.mobileSrc !== undefined ? imageProps.mobileSrc : typeof imageProps.src === 'string' ? imageProps.src : undefined}
                                    mobileWidth={imageProps.mobileWidth !== undefined ? imageProps.mobileWidth : 35}
                                    mobileHeight={imageProps.mobileHeight !== undefined ? imageProps.mobileHeight : 170}
                                />
                            </figure>
                        )
                })}
            </C.Card>
            {pageContent && pageContent["customizations.link"]?.data && <C.PageLink href={customHref ?? "/classic-customizations"} variant={C.PageLinkVariant.Link} className={css["mobile-see-page-link"]}>
                {pageContent["customizations.link"].data}
            </C.PageLink>}
        </C.PageSection>
    );
}